import { inject, Injectable } from '@angular/core';
import { ProjectSummary } from '../models/project-summary.model';
import { ProjectPick } from '../models/project-pick.model';
import {
    getProjectOrderNumber,
    InventoryBundleStore,
    PickRead,
    PickStore,
    ProjectStore,
} from '@pf/platform-wms/domain';
import { orderBy } from 'lodash-es';
import { asArraySafe, asNumberSafe } from '@pf/shared-utility';
import {
    EquipmentTypeFragment,
    InventoryBundleFragment,
    PickLocationFragment,
    PickStatus,
    PickViewModelRead,
    ProjectLineItemFragment,
    ProjectViewModelRead,
} from '@control-tower/platform-wms';
import { IEntity, IEntityFragment } from '@pf/shared-common';
import { ItemStore } from '@pf/platform-item-catalog/domain';
import { isHandlingLtl } from '../models/cpw-equipment-types';

@Injectable()
export class ProjectPickListConsolidationFactory {
    private readonly projectStore = inject(ProjectStore);
    private readonly pickStore = inject(PickStore);
    private readonly inventoryBundleStore = inject(InventoryBundleStore);
    private readonly itemStore = inject(ItemStore);

    consolidatePicks(
        taskId: string,
        projectSummaries: ProjectSummary[]
    ): ProjectPick[] {
        const projectPicks = projectSummaries
            .map(summary => this.projectStore.get(summary.projectId))
            .reduce((picks, project) => {
                const projectLineItems = orderBy(
                    asArraySafe(project.lineItems),
                    ['lineNumber'],
                    ['asc']
                );

                if (projectLineItems.length === 0) {
                    console.error(
                        `No project line items found for project ${project.id}`
                    );
                    return picks;
                }

                const projectPicks = this.getProjectPicksFromProjectLineItems(
                    taskId,
                    project,
                    projectLineItems
                );
                picks.push(...projectPicks);

                return picks;
            }, [] as ProjectPick[]);

        return this.sortProjectPicks(projectPicks);
    }

    private sortProjectPicks(projectPicks: ProjectPick[]) {
        return orderBy(
            projectPicks,
            [
                'pickedDate',
                'isLtl',
                'inventoryBundle.container.slot.zone.zonePriority', // Zone
                'inventoryBundle.container.slot.slotPriority', // Slot
            ],
            ['desc', 'desc', 'asc', 'asc']
        ).sort(a => (a.status !== PickStatus.Available ? -1 : 1));
    }

    private getProjectPicksFromProjectLineItems(
        taskId: string,
        project: ProjectViewModelRead & IEntity,
        projectLineItems: ProjectLineItemFragment[]
    ) {
        const picks: ProjectPick[] = [];
        projectLineItems.forEach(lineItem => {
            const pick =
                this.pickStore.get(lineItem.pickId as string) ||
                (lineItem.pick as PickRead);

            if (!pick) {
                console.error(
                    `No pick found for project line item ${lineItem.id}`
                );
                return;
            }

            if (!lineItem.item) {
                lineItem.item = this.itemStore.get(lineItem.itemId as string);
            }

            asArraySafe(pick.pickLocations).forEach(pickLocation => {
                if (
                    pickLocation.inventoryBundleId &&
                    !pickLocation.inventoryBundle
                ) {
                    pickLocation.inventoryBundle =
                        this.inventoryBundleStore.get(
                            pickLocation.inventoryBundleId as string
                        );

                    if (
                        pickLocation.inventoryBundle &&
                        !pickLocation.inventoryBundle.item
                    ) {
                        pickLocation.inventoryBundle.item = pick.item;
                    }
                }
                picks.push(
                    this.mapToProjectPick(
                        taskId,
                        project,
                        lineItem,
                        projectLineItems,
                        pick,
                        pickLocation
                    )
                );
            });
        });

        return picks;
    }

    private getEquipmentType(
        pickLocation: PickLocationFragment
    ): EquipmentTypeFragment | null {
        const pickContainer = pickLocation.inventoryBundle?.container;
        if (!pickContainer) {
            return null;
        }

        if (!pickContainer.slot) {
            return pickContainer.equipment?.equipmentType || null;
        }
        return pickContainer.slot.equipmentType || null;
    }

    private mapToProjectPick(
        taskId: string,
        project: ProjectViewModelRead & IEntity,
        lineItem: ProjectLineItemFragment,
        projectLineItems: ProjectLineItemFragment[],
        pick: PickViewModelRead & IEntity,
        pickLocation: PickLocationFragment
    ) {
        const equipmentType = this.getEquipmentType(pickLocation);
        const isLtl = isHandlingLtl(equipmentType);
        return {
            taskId: taskId,
            projectId: project.id!,
            warehouseId: project.warehouse!.id!,
            projectLineItemId: lineItem.id!,
            projectNumber: getProjectOrderNumber(project),
            projectLineItemNumber: lineItem.lineNumber!,
            maxProjectLineItemNumber:
                projectLineItems[projectLineItems.length - 1].lineNumber!,
            item: lineItem.item!,
            pickId: pick!.id!,
            pickLocationId: pickLocation.id!,
            pickedDate: pickLocation.pickedDate,
            status: pickLocation.status || PickStatus.Available,
            quantityNeeded: asNumberSafe(pickLocation.quantityNeeded),
            quantityPicked: pickLocation.pickedQuantity!,
            pickToContainerId: pickLocation.pickToContainerId,
            inventoryBundle:
                pickLocation.inventoryBundle! as InventoryBundleFragment &
                    IEntityFragment,
            equipmentType,
            isLtl,
        } as ProjectPick;
    }
}
